import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createVNode as _createVNode, withCtx as _withCtx, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = {
  class: "flex flex-wrap gap-2",
  "data-testid": "filter-group"
}
const _hoisted_2 = ["data-testid", "onClick"]
const _hoisted_3 = { class: "text-gray-60 group-hover:text-gray-70 flex items-center overflow-hidden whitespace-nowrap text-xs font-medium duration-200 ease-out" }
const _hoisted_4 = { class: "truncate" }
const _hoisted_5 = {
  key: 1,
  class: "text-sm font-medium text-gray-50"
}
const _hoisted_6 = { key: 2 }
const _hoisted_7 = {
  key: 0,
  class: "text-gray-60 pointer-events-none text-xs"
}
const _hoisted_8 = {
  key: 1,
  class: "flex items-center gap-2"
}

import { PlusIcon } from '@klausapp/ui-kit/icons'
import { UiDropdownV2 as UiDropdown, UiMenuFilterTrigger } from '@klausapp/ui-kit'
import { Modes, SelectAllModes } from '@klausapp/ui-kit/components/UiDropdownV2/types'
import { computed, nextTick, ref } from 'vue'
import { matchSorter } from 'match-sorter'
import IntelligenceIcon from '@/assets/intelligence-icon.svg'
import i18n from '@/i18n'
import BotIcon from '@/assets/bot.svg'
import { session } from '@/composables/useSession'
import FilterItem from './FilterItem.vue'
import type { FilterItemEmit, FilterOption, AddedFilter, DropdownSelectedValue } from './types'


export default /*@__PURE__*/_defineComponent({
  __name: 'FilterGroup',
  props: {
    addedFilters: {},
    filterOptions: {},
    showAll: { type: Boolean },
    searchFilter: {},
    hasPagination: { type: Boolean },
    total: {},
    error: { type: Boolean }
  },
  emits: ["filter-adding-started", "filter-applied", "add-similar-filter", "remove", "search-change", "load-more"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props

const emit = __emit

const filterRef = ref()
const dropdownRef = ref()
const showDropdown = () => dropdownRef.value.show()
const searchQuery = ref('')

const allFilters = computed<(FilterOption | AddedFilter)[]>(() => {
  const addedFilters = props.addedFilters.map((f) => ({ ...f, addedFilter: true }))

  if (props.showAll) {
    const filterOptions = props.filterOptions.filter((f) => !props.addedFilters.find(({ name }) => name === f.name))
    const allOptions = [...addedFilters, ...filterOptions].sort((a, b) => a.label.localeCompare(b.label))
    const filteredOptions = props.searchFilter?.length
      ? matchSorter(allOptions, props.searchFilter, { keys: ['label'] })
      : allOptions
    return filteredOptions
  }

  return addedFilters
})

const filterAddingStarted = async (o: FilterOption) => {
  emit('filter-adding-started', o)
  await nextTick()
  if (!filterRef.value) return

  const i = filterRef.value.length - 1
  filterRef.value[i]?.$el.click()
}

const filterApplied = (id: string, f: FilterItemEmit) => {
  emit('filter-applied', id, f)
}

const addSimilarFilter = (f: FilterItemEmit, filterName: string) => {
  const o = props.filterOptions.find((f) => f.name === filterName)
  emit('add-similar-filter', f, o)
}

const isAddedFilterObject = (obj: unknown): obj is AddedFilter => {
  return typeof obj === 'object' && obj !== null && 'valueType' in obj
}

const handleSearchChange = (search: string) => {
  searchQuery.value = search
  emit('search-change', search)
}

const showAddButton = computed(() => {
  return (props.filterOptions.length && !props.showAll) || props.hasPagination
})

const hideDescriptionTooltip = (f: FilterOption | AddedFilter) => 'isSpotlight' in f && f.isSpotlight

const selectedValuesTooltip = (f: FilterOption | AddedFilter) => {
  if (!isAddedFilterObject(f)) return null

  if (
    f.name === 'TICKET_STATUS' &&
    session.features.promptTestingDev &&
    !!f.disabled &&
    (f?.value as DropdownSelectedValue[])?.length > 1
  ) {
    return i18n.t('settings.ai.conversations_filter_modal.multiple_disabled_selected', {
      options: (f.value as DropdownSelectedValue[]).map((v) => v.label).join(', '),
    })
  }

  return null
}

const selectedValuesTooltipMap = computed(() =>
  allFilters.value.reduce<Record<string, string | null>>((acc, f) => {
    acc[f.name] = selectedValuesTooltip(f)
    return acc
  }, {}),
)

__expose({ showDropdown })

return (_ctx: any,_cache: any) => {
  const _directive_tippy = _resolveDirective("tippy")!

  return (_openBlock(), _createElementBlock("ul", _hoisted_1, [
    ((_ctx.showAll && allFilters.value.length) || (!_ctx.showAll && _ctx.addedFilters.length))
      ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(allFilters.value, (f) => {
          return _withDirectives((_openBlock(), _createElementBlock("li", {
            key: f.label,
            class: "overflow-hidden"
          }, [
            (isAddedFilterObject(f) && f.addedFilter)
              ? (_openBlock(), _createBlock(FilterItem, {
                  key: 0,
                  ref_for: true,
                  ref_key: "filterRef",
                  ref: filterRef,
                  "filter-id": f.name,
                  "filter-label": f.label,
                  "show-add-similar-button": f.showAddSimilarButton,
                  description: f.description,
                  conditions: f.conditions,
                  "selected-condition": f.selectedCondition,
                  value: f.value,
                  "value-type": f.valueType,
                  "value-component-props": f.valueComponentProps,
                  "intelligence-icon": f.intelligenceIcon,
                  "bot-icon": f.botIcon,
                  "force-show-remove-button": !_ctx.showAll,
                  "force-hide-remove-button": f.forceHideRemoveButton,
                  status: f.status,
                  "is-spotlight": f.isSpotlight,
                  disabled: !!f.disabled,
                  onRemove: ($event: any) => (_ctx.$emit('remove', f.id)),
                  onFilterApplied: ($event: any) => (filterApplied(f.id, $event)),
                  onAddSimilarFilter: ($event: any) => (addSimilarFilter($event, f.name))
                }, null, 8 /* PROPS */, ["filter-id", "filter-label", "show-add-similar-button", "description", "conditions", "selected-condition", "value", "value-type", "value-component-props", "intelligence-icon", "bot-icon", "force-show-remove-button", "force-hide-remove-button", "status", "is-spotlight", "disabled", "onRemove", "onFilterApplied", "onAddSimilarFilter"]))
              : _withDirectives((_openBlock(), _createElementBlock("button", {
                  key: 1,
                  "data-testid": `filter-item-${f.name}`,
                  class: _normalizeClass(["hover:bg-gray-30 group flex h-7 max-w-full cursor-pointer items-center rounded-md px-3 duration-200 ease-out", f.isIntelligent ? _ctx.$style.holo : 'bg-gray-20']),
                  type: "button",
                  onClick: _withModifiers(($event: any) => (!isAddedFilterObject(f) && filterAddingStarted(f)), ["prevent"])
                }, [
                  _createElementVNode("span", _hoisted_3, [
                    (f.intelligenceIcon)
                      ? (_openBlock(), _createBlock(_unref(IntelligenceIcon), {
                          key: 0,
                          class: "mr-2 h-3.5 shrink-0"
                        }))
                      : (f.botIcon)
                        ? (_openBlock(), _createBlock(_unref(BotIcon), {
                            key: 1,
                            class: "mr-2 h-4 shrink-0"
                          }))
                        : _createCommentVNode("v-if", true),
                    _createElementVNode("span", _hoisted_4, _toDisplayString(f.label), 1 /* TEXT */)
                  ])
                ], 10 /* CLASS, PROPS */, _hoisted_2)), [
                  [_directive_tippy, { content: !hideDescriptionTooltip(f) ? f.description : undefined }]
                ])
          ])), [
            [_directive_tippy, {
          content: selectedValuesTooltipMap.value[f.name],
        }]
          ])
        }), 128 /* KEYED_FRAGMENT */))
      : (_ctx.showAll && !allFilters.value.length && _ctx.searchFilter?.length)
        ? (_openBlock(), _createElementBlock("li", _hoisted_5, _toDisplayString(_ctx.$t('universal.no_items_found')), 1 /* TEXT */))
        : _createCommentVNode("v-if", true),
    (showAddButton.value)
      ? (_openBlock(), _createElementBlock("li", _hoisted_6, [
          _createVNode(_unref(UiDropdown), {
            ref_key: "dropdownRef",
            ref: dropdownRef,
            mode: _unref(Modes).Single,
            "close-on-select": "",
            "model-value": [],
            "max-width": 999,
            disabled: false,
            options: _ctx.filterOptions,
            "search-placeholder": _ctx.$t('universal.search'),
            "no-results-text": _ctx.$t('universal.no_items_found'),
            "has-search": "",
            size: "lg",
            "filter-results": !_ctx.hasPagination,
            "select-all-mode": _unref(SelectAllModes).None,
            "is-object": true,
            total: _ctx.total,
            label: "label",
            "prop-to-check": "name",
            class: "group outline-0",
            "append-to-document": "",
            onSearchChange: handleSearchChange,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (filterAddingStarted($event)))
          }, {
            trigger: _withCtx(() => [
              _createVNode(_unref(UiMenuFilterTrigger), {
                "show-chevron": false,
                "full-width": false,
                size: "md",
                error: _ctx.error
              }, {
                icon: _withCtx(() => [
                  _createVNode(_unref(PlusIcon), { class: "icon-m" })
                ]),
                _: 1 /* STABLE */
              }, 8 /* PROPS */, ["error"])
            ]),
            option: _withCtx(({ option }) => [
              (option.name === 'placeholder')
                ? (_openBlock(), _createElementBlock("div", _hoisted_7, _toDisplayString(option.label), 1 /* TEXT */))
                : (option.intelligenceIcon)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                      _createTextVNode(_toDisplayString(option.label) + " ", 1 /* TEXT */),
                      _createVNode(_unref(IntelligenceIcon), { class: "icon-s shrink-0" })
                    ]))
                  : _createCommentVNode("v-if", true)
            ]),
            "more-count": _withCtx(({ count }) => [
              (!searchQuery.value)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    type: "button",
                    class: "hover:text-gray-60",
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('load-more')))
                  }, _toDisplayString(_ctx.$t('universal.n_more_simple', { n: _ctx.$n(count) }, count)), 1 /* TEXT */))
                : _createCommentVNode("v-if", true)
            ]),
            _: 1 /* STABLE */
          }, 8 /* PROPS */, ["mode", "options", "search-placeholder", "no-results-text", "filter-results", "select-all-mode", "total"])
        ]))
      : _createCommentVNode("v-if", true)
  ]))
}
}

})